import { TicketListCandidateEntity } from '../../../../../domain/entity/TicketListEntity/index'
import { selectTicketListsForDisplay } from '../../../../../domain/entity/TicketListEntity'
import { FONT_FAMILY } from '../../../../../styles/commonStyles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AutocompleteRenderInputParams } from '@mui/material'
import { intl } from '../../../../../i18n'
import { addGlobalMessage, MessageLevel } from '../../../../../store/messages'
import { AllState } from '../../../../../store'
import { useTicketListRepository } from '../../../../../services/adaptors/ticketListRepositoryAdaptor'
import { WbsItemTypeVO } from '../../../../../domain/value-object/WbsItemTypeVO'
import { ReferencedEntity } from '../../../../../domain/value-object/ReferencedEntity'
import { measureTextWidthPx } from '../../../../commons/textWidth'
import { renderInputWithIcon } from '../../../../components/inputs/Autocomplete/variants'
import { TicketListOption } from '../../../../features/TicketListOption'
import { TicketTypeGroupLabel } from '../../../../features/TicketListOption/TicketTypeGroupLabel'
import { TicketListShowClosedToggle } from '../../../../features/TicketListShowClosedToggle'

// ポップアップ幅の算出に使う値。選択肢は fontSize 14px、グループ見出しは 12px の太字。
const OPTION_FONT = `14px ${FONT_FAMILY}`
const GROUP_LABEL_FONT = `700 12px ${FONT_FAMILY}`
const OPTION_FIXED_AREA_PX = 134 // ステータスの丸印 + 余白
const GROUP_LABEL_FIXED_AREA_PX = 60 // 開閉アイコン + タイプアイコン + 余白
const MIN_POPPER_WIDTH_PX = 320
const MAX_POPPER_WIDTH_PX = 400

export const useTicketListSearch = (
  projectUuid: string,
  ticketType: string,
  selectedTicketListUuid: string | undefined
) => {
  const { fetchListAsReferencedEntity } = useTicketListRepository()
  const [candidates, setCandidates] = useState<TicketListCandidateEntity[]>([])
  const ticketTypes = useSelector<AllState, WbsItemTypeVO[]>(
    state => state.project.ticketTypes
  )
  const [showsClosed, setShowsClosed] = useState<boolean>(false)
  const dispatch = useDispatch()

  // 失敗のたびにトーストを積むと、再試行を繰り返す間に同じ内容が重なる。連続した失敗では
  // 最初の1回だけ知らせ、取得できたら次の失敗をまた知らせる。
  const hasNotifiedFetchFailure = useRef<boolean>(false)
  const loadCandidates = useCallback(async () => {
    try {
      setCandidates(await fetchListAsReferencedEntity(projectUuid))
      hasNotifiedFetchFailure.current = false
    } catch (err) {
      if (hasNotifiedFetchFailure.current) return
      hasNotifiedFetchFailure.current = true
      dispatch(
        addGlobalMessage({
          type: MessageLevel.ERROR,
          title: intl.formatMessage({
            id: 'ticketList.select.error.fetchFailed',
          }),
        })
      )
    }
  }, [dispatch, fetchListAsReferencedEntity, projectUuid])

  // 選択済みの管理表のアイコンを初期表示から出すため、フォーカスを待たずに候補を読む。
  useEffect(() => {
    loadCandidates()
  }, [loadCandidates])

  // 取得に失敗すると候補が空のままになり、必須項目であるチケット管理表を選べなくなる。
  // 空のときはフォーカスのたびに取り直して復帰できるようにする。
  const reloadCandidatesIfEmpty = useCallback(() => {
    if (candidates.length === 0) {
      loadCandidates()
    }
  }, [candidates.length, loadCandidates])

  // 並び順の比較のたびに引くため、codeで直接引けるようにしておく。
  const ticketTypeByCode = useMemo(
    () => new Map(ticketTypes.map(ticketType => [ticketType.code, ticketType])),
    [ticketTypes]
  )
  const findTicketType = useCallback(
    (code: string) => ticketTypeByCode.get(code),
    [ticketTypeByCode]
  )
  const toTicketTypeName = useCallback(
    (code: string) => findTicketType(code)?.name ?? code,
    [findTicketType]
  )

  // 完了・破棄済みは既定で隠したうえで、編集中のチケットと同じチケットタイプを先頭に、
  // タイプ内は業務上の進行順に並べる。他タイプへの付け替えも許容されている操作なので、
  // 選択肢自体は除外しない。
  const ticketListOptions = useMemo(
    () =>
      selectTicketListsForDisplay(candidates, candidate => candidate, {
        showsClosed,
        selectedUuid: selectedTicketListUuid,
        typeSort: {
          currentTicketType: ticketType,
          ticketTypeNameOf: toTicketTypeName,
        },
      }),
    [
      candidates,
      showsClosed,
      selectedTicketListUuid,
      ticketType,
      toTicketTypeName,
    ]
  )

  // グループ分け・選択肢・入力欄の描画のたびに引くため、uuidで直接引けるようにしておく。
  const candidateByUuid = useMemo(
    () => new Map(candidates.map(candidate => [candidate.uuid, candidate])),
    [candidates]
  )
  const findCandidate = useCallback(
    (option: ReferencedEntity) => candidateByUuid.get(option.uuid),
    [candidateByUuid]
  )

  // 表示名はユーザーが編集でき一意とは限らないため、グループのキーには code を使う。
  const groupTicketListByType = useCallback(
    (option: ReferencedEntity) => findCandidate(option)?.ticketType ?? '',
    [findCandidate]
  )

  const renderTicketListOption = useCallback(
    (option: ReferencedEntity) => {
      const candidate = findCandidate(option)
      if (!candidate) return <>{option.name}</>
      return (
        <TicketListOption displayName={option.name} status={candidate.status} />
      )
    },
    [findCandidate]
  )

  // アイコンは各管理表ではなくグループ見出しに置く。同じグループ内では同じタイプが続くため。
  const renderTicketTypeGroupLabel = useCallback(
    (code: string) => (
      <TicketTypeGroupLabel
        name={toTicketTypeName(code)}
        iconUrl={findTicketType(code)?.iconUrl}
      />
    ),
    [toTicketTypeName, findTicketType]
  )

  const renderTicketListInput = useCallback(
    (
      params: AutocompleteRenderInputParams,
      value: ReferencedEntity | undefined
    ) => {
      const candidate = value ? findCandidate(value) : undefined
      const iconUrl = candidate
        ? findTicketType(candidate.ticketType)?.iconUrl
        : undefined
      return renderInputWithIcon(
        params,
        value ? { ...value, iconUrl } : undefined
      )
    },
    [findCandidate, findTicketType]
  )

  const listFooter = useMemo(
    () => (
      <TicketListShowClosedToggle
        checked={showsClosed}
        onChange={setShowsClosed}
      />
    ),
    [showsClosed]
  )

  // 折りたたみ中のグループの項目はDOMに無いため、全項目が収まる幅を先に決めて固定する。
  // 管理表名だけでなくグループ見出しも収める必要がある。
  const popperWidth = useMemo(() => {
    const maxNameWidth = candidates.reduce(
      (max, candidate) =>
        Math.max(max, measureTextWidthPx(candidate.name, OPTION_FONT)),
      0
    )
    const maxGroupLabelWidth = candidates.reduce(
      (max, candidate) =>
        Math.max(
          max,
          measureTextWidthPx(
            toTicketTypeName(candidate.ticketType),
            GROUP_LABEL_FONT
          )
        ),
      0
    )
    return Math.min(
      MAX_POPPER_WIDTH_PX,
      Math.max(
        MIN_POPPER_WIDTH_PX,
        OPTION_FIXED_AREA_PX + maxNameWidth,
        GROUP_LABEL_FIXED_AREA_PX + maxGroupLabelWidth
      )
    )
  }, [candidates, toTicketTypeName])

  // セル定義のメモ化を効かせるため、毎描画で新しい配列を作らない。
  const defaultExpandedGroups = useMemo(
    () => (ticketType ? [ticketType] : undefined),
    [ticketType]
  )

  return {
    options: ticketListOptions,
    onFocus: reloadCandidatesIfEmpty,
    groupBy: groupTicketListByType,
    renderOption: renderTicketListOption,
    renderGroupLabel: renderTicketTypeGroupLabel,
    renderInput: renderTicketListInput,
    defaultExpandedGroups,
    popperWidth,
    listFooter,
  }
}
